/**
 * Globaler Scroll-To-Top Button.
 * Erscheint wenn der Nutzer > 300px gescrollt hat.
 * Arbeitet sowohl mit .dash-main (Dashboard) als auch mit window (alle anderen Seiten).
 *
 * Bugfix: ab 1280px hat .dash-body eine dritte Grid-Spalte (rechte Sidebar, 260px,
 * dashboard.css:280). Ein reines `right: 1rem` (relativ zum Viewport) hätte den Button dort
 * über die Sidebar statt über den eigentlichen Content-Bereich gelegt. repositionButton()
 * berechnet den Rechts-Abstand stattdessen relativ zur echten rechten Kante von .dash-main.
 */
import { DESKTOP_LAYOUT_QUERY } from '../utils/mobile-layout.js';

/** Reine Testfunktion: gibt true zurück wenn scrollPosition den threshold überschreitet. */
export function isScrolledPastThreshold(scrollPosition: number, threshold = 300): boolean {
  return scrollPosition > threshold;
}

let btn: HTMLButtonElement | null = null;

function getScrollContainer(): Element | null {
  return document.querySelector('.dash-main') ?? null;
}

function createButton(): void {
  if (document.getElementById('scroll-top-btn')) return;

  const s = document.createElement('style');
  s.id = 'scroll-top-styles';
  s.textContent = `
    #scroll-top-btn {
      position: fixed;
      /* Über der Bottom-Nav inkl. Safe-Area (docs/MOBILE.md S2/S3). */
      bottom: calc(var(--bottom-nav-height, 60px) + var(--bottom-nav-safe, 8px) + 1rem);
      right: 1rem;
      z-index: 201;
      width: 44px; height: 44px;
      font-family: var(--font-mono); font-size: 18px; line-height: 1;
      color: var(--accent-gold);
      background: var(--bg-elevated);
      border: 1px solid var(--border);
      border-radius: 6px;
      cursor: pointer;
      display: flex; align-items: center; justify-content: center;
      touch-action: manipulation;
      opacity: 0; pointer-events: none;
      transition: opacity 150ms ease;
    }
    #scroll-top-btn.is-visible {
      opacity: 1; pointer-events: auto;
    }
    @media ${DESKTOP_LAYOUT_QUERY} {
      #scroll-top-btn {
        bottom: 1rem;
      }
    }
  `;
  document.head.appendChild(s);

  btn = document.createElement('button');
  btn.id = 'scroll-top-btn';
  btn.setAttribute('aria-label', 'Nach oben scrollen');
  btn.setAttribute('type', 'button');
  btn.textContent = '↑';
  document.body.appendChild(btn);

  btn.addEventListener('click', () => {
    const el = getScrollContainer();
    if (el) {
      el.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  });
}

// ─── Positionierung relativ zu .dash-main ────────────────────────────────────

/**
 * Setzt den right-Abstand relativ zur echten rechten Kante von .dash-main statt zum
 * Viewport — sonst läge der Button ab 1280px über der dritten Grid-Spalte (rechte Sidebar,
 * 260px) statt im Content-Fenster. Ohne .dash-main (Nicht-Dashboard-Seiten) bleibt der
 * CSS-Default (1rem vom Viewport) unangetastet.
 */
function repositionButton(): void {
  if (!btn) return;
  const container = getScrollContainer();
  if (!container) {
    btn.style.removeProperty('right');
    return;
  }
  const rightInset = window.innerWidth - container.getBoundingClientRect().right;
  btn.style.right = `calc(${String(Math.max(rightInset, 0))}px + 1rem)`;
}

let resizeListenerAttached = false;

function attachResizeListener(): void {
  if (resizeListenerAttached) return;
  resizeListenerAttached = true;
  window.addEventListener('resize', repositionButton, { passive: true });
}

// ─── Container-basierter Scroll-Listener ─────────────────────────────────────

let currentContainer: Element | null = null;
let containerListener: (() => void) | null = null;

function attachToContainer(container: Element): void {
  if (currentContainer === container) return;

  if (currentContainer && containerListener) {
    currentContainer.removeEventListener('scroll', containerListener);
  }

  currentContainer = container;
  containerListener = () => {
    if (!btn) return;
    btn.classList.toggle('is-visible', isScrolledPastThreshold((container as HTMLElement).scrollTop));
  };
  container.addEventListener('scroll', containerListener, { passive: true });
}

// ─── Window-basierter Scroll-Listener (für Seiten ohne .dash-main) ──────────

let windowListenerAttached = false;

function attachWindowListener(): void {
  if (windowListenerAttached) return;
  windowListenerAttached = true;

  window.addEventListener('scroll', () => {
    if (!btn) return;
    // Nur aktiv wenn kein .dash-main vorhanden (sonst Container-Listener zuständig)
    if (document.querySelector('.dash-main')) return;
    btn.classList.toggle('is-visible', isScrolledPastThreshold(window.scrollY));
  }, { passive: true });
}

/** Scroll-to-top Button initialisieren. */
export function initScrollToTop(): void {
  createButton();
  attachWindowListener();
  attachResizeListener();
  repositionButton();
}

/** Scroll-Container neu verknüpfen (bei Tab-Wechsel aufrufen). */
export function updateScrollToTopContainer(): void {
  const container = getScrollContainer();
  if (container) {
    attachToContainer(container);
    // Button zurücksetzen wenn Container gewechselt
    if (btn) btn.classList.remove('is-visible');
  }
  // renderAll() baut .dash-main bei jedem Aufruf neu auf (neue Breite/Position möglich,
  // z.B. bei Sidebar-Inhaltsänderungen) — Position also bei jedem Aufruf neu berechnen.
  repositionButton();
}
